import { ResolutionString, SubscribeBarsCallback } from 'charting_library';
import { Logical, Time } from 'lightweight-charts';

import { ECexName } from 'web3';

import { ETradeDirection } from './trade-base';

export type IPriceFormat = 'usd' | 'token';

export enum EChartFormat {
  Percentage = 'percentage',
  Normal = 'normal',
}

type INumber = number;

type IHoursRange = `${INumber}H`;
type IDaysRange = `${INumber}D`;
type IWeeksRange = `${INumber}W`;
type IMonthsRange = `${INumber}M`;
type IYearsRange = `${INumber}Y`;
type IAllRange = 'ALL';

export type IChartRange =
  | IHoursRange
  | IDaysRange
  | IWeeksRange
  | IMonthsRange
  | IYearsRange
  | IAllRange;

export type IChartTimeframe =
  | '1s'
  | '10s'
  | '30s'
  | '1m'
  | '3m'
  | '5m'
  | '15m'
  | '30m'
  | '1h'
  | '2h'
  | '4h'
  | '6h'
  | '8h'
  | '12h'
  | '24h'
  | '1d'
  | '2d'
  | '3d'
  | '5d'
  | '168h'
  | '1w'
  | '1M';

//                  time,    open,   high,   low,    close,  volume
export type KLine = [number, string, string, string, string, string];

export type Trade = {
  price: string;
  quantity: string;
  timestamp: string;
  side: string;
};

export interface ICexChartPoint {
  time: number;
  close: number;
  high: number;
  low: number;
  open: number;
  volume: number;
  baseBalance: number;
  baseBalanceUsd: number;
  quoteBalane: number;
  quoteBalanceUsd: number;
  internalVolume: number;
  balanceTotalUsd: number;
}

export interface BalanceChartPoint {
  time: number;
  baseBalance: number;
  baseBalanceUsd: number;
  quoteBalane: number;
  quoteBalanceUsd: number;
  balanceTotalUsd: number;
}

export interface BalanceLineData {
  time: number;
  line1: number; // Base Balance USD
  line2: number; // Quote Balance USD
  line3: number; // Total Balance USD
}

export interface IChartOrder {
  price: number;
  amount: string;
  side: ETradeDirection;
  label: string;
  account_id: number;
  pair_id: number;
  cex_order_id: string;
}

export enum ETradingViewEvents {
  UpdateRecentBars = 'tvupdaterecentbars',
  UpdateBalanceData = 'tvupdatebalancedata',
}

export interface TVSubscribeParams {
  listenerGuid: string;
  resolution: ResolutionString;
  onTick: SubscribeBarsCallback;
  onResetCacheNeededCallback: () => void;
}

export interface TVUnsubscribeParams {
  listenerGuid: string;
}

export type ICursorPosition = { time: Time; logical: Logical | undefined } | undefined;

export type ILogicalRange = { from: number; to: number } | undefined;

export type ITimescaleRange = { from: number; to: number } | undefined;

export const mapChartRangeToRangeAPI = (range: IChartRange): IChartRange => {
  if (range === 'ALL') return 'ALL';

  const rangeNumber = Number(
    range
      .split('')
      .filter(el => el.match(/^\d$/))
      .join(''),
  );

  // is hours
  if (range.includes('H')) {
    return `${rangeNumber}H`;
  }

  // is days
  if (range.includes('D')) {
    return `${rangeNumber}D`;
  }

  // is weeks
  if (range.includes('W')) {
    return `${rangeNumber}W`;
  }

  // is month
  if (range.includes('M')) {
    return `${rangeNumber}M`;
  }

  // is year
  if (range.includes('Y')) {
    return `${rangeNumber}Y`;
  }

  return `${rangeNumber}` as IChartRange;
};

export const mapChartRangeToCaption = (range: IChartRange) => {
  if (range === 'ALL') return 'ALL';

  const rangeNumber = Number(
    range
      .split('')
      .filter(el => el.match(/^\d$/))
      .join(''),
  );

  // is hours
  if (range.includes('H')) {
    return `${rangeNumber}H`;
  }

  // is days
  if (range.includes('D')) {
    return `${rangeNumber}D`;
  }

  // is weeks
  if (range.includes('W')) {
    return `${rangeNumber}W`;
  }

  // is month
  if (range.includes('M')) {
    return `${rangeNumber}M`;
  }

  // is year
  if (range.includes('Y')) {
    return `${rangeNumber}Y`;
  }
};

export const mapChartTimeframeToCaption = (chartTimeframe: IChartTimeframe) => {
  const mapper: Partial<Record<IChartTimeframe, string>> = {
    '1s': '1S',
    '10s': '10S',
    '30s': '30S',
    '1m': '1m',
    '3m': '3m',
    '5m': '5m',
    '15m': '15m',
    '30m': '30m',
    '1h': '1H',
    '2h': '2H',
    '4h': '4H',
    '6h': '6H',
    '8h': '8H',
    '12h': '12H',
    '24h': '1D',
    '1d': '1D',
    '2d': '2D',
    '3d': '3D',
    '5d': '5D',
    '168h': '1W',
    '1w': '1W',
    '1M': '1M',
  };

  return mapper[chartTimeframe] ?? chartTimeframe.toUpperCase();
};

const mergeIntervals = (
  base: Record<ECexName, any>,
  overrides: Partial<Record<ECexName, any>>,
): Record<ECexName, any> => ({
  ...base,
  ...overrides,
});

const map1m: Record<ECexName, any> = {
  [ECexName.gate]: '1m',
  [ECexName.mexc]: '1m',
  [ECexName.bitget]: '1min',
  [ECexName.kucoin]: '1min',
  [ECexName.okx]: '1m',
  [ECexName.bybit]: 1,
  [ECexName.bitmart]: 1,
  [ECexName.bithumb]: '1',
  [ECexName.binance]: '1m',
  [ECexName.bingx]: '1m',
  [ECexName.xt]: '1m',
  [ECexName.lbank]: 'minute1',
  [ECexName.htx]: '1min',
  [ECexName.whitebit]: '1m',
  [ECexName.coinbase]: 60,
  [ECexName.cryptocom]: '1m',
  [ECexName.upbit]: '1',
  [ECexName.bitstamp]: 60,
  [ECexName.bitvavo]: '1m',
  [ECexName.toobit]: '1m',
  [ECexName.hashkey]: '1m',
  [ECexName.bullish]: '1m',
  [ECexName.gemini]: '1m',
  [ECexName.bitfinex]: '1m',
  [ECexName.coinstore]: '1min',
  [ECexName.hotcoin]: '1min',
  [ECexName.kraken]: 1,
  [ECexName.indodax]: '',
  [ECexName.weex]: '1min',
  [ECexName.biconomy]: '1min',
  [ECexName.coinw]: '60',
  [ECexName.dextrade]: 1,
  [ECexName.digifinex]: '1',
  [ECexName.btse]: '1',
};

const map5m: Record<ECexName, any> = {
  [ECexName.gate]: '5m',
  [ECexName.mexc]: '5m',
  [ECexName.bitget]: '5min',
  [ECexName.kucoin]: '5min',
  [ECexName.okx]: '5m',
  [ECexName.bybit]: 5,
  [ECexName.bitmart]: 5,
  [ECexName.bithumb]: '5',
  [ECexName.binance]: '5m',
  [ECexName.bingx]: '5m',
  [ECexName.xt]: '5m',
  [ECexName.lbank]: 'minute5',
  [ECexName.htx]: '5min',
  [ECexName.whitebit]: '5m',
  [ECexName.coinbase]: 300,
  [ECexName.cryptocom]: '5m',
  [ECexName.upbit]: '5',
  [ECexName.bitstamp]: 300,
  [ECexName.bitvavo]: '5m',
  [ECexName.toobit]: '5m',
  [ECexName.hashkey]: '5m',
  [ECexName.bullish]: '5m',
  [ECexName.gemini]: '5m',
  [ECexName.bitfinex]: '5m',
  [ECexName.coinstore]: '5min',
  [ECexName.hotcoin]: '5mins',
  [ECexName.kraken]: 5,
  [ECexName.indodax]: '',
  [ECexName.weex]: '5min',
  [ECexName.biconomy]: '5min',
  [ECexName.coinw]: '300',
  [ECexName.dextrade]: 5,
  [ECexName.digifinex]: '5',
  [ECexName.btse]: '5',
};

const map15m: Record<ECexName, any> = {
  [ECexName.gate]: '15m',
  [ECexName.mexc]: '15m',
  [ECexName.bitget]: '15min',
  [ECexName.kucoin]: '15min',
  [ECexName.okx]: '15m',
  [ECexName.bybit]: 15,
  [ECexName.bitmart]: 15,
  [ECexName.bithumb]: '15',
  [ECexName.binance]: '15m',
  [ECexName.bingx]: '15m',
  [ECexName.xt]: '15m',
  [ECexName.lbank]: 'minute15',
  [ECexName.htx]: '15min',
  [ECexName.whitebit]: '15m',
  [ECexName.coinbase]: 900,
  [ECexName.cryptocom]: '15m',
  [ECexName.upbit]: '15',
  [ECexName.bitstamp]: 900,
  [ECexName.bitvavo]: '15m',
  [ECexName.toobit]: '15m',
  [ECexName.hashkey]: '15m',
  [ECexName.bullish]: undefined,
  [ECexName.gemini]: '15m',
  [ECexName.bitfinex]: '15m',
  [ECexName.coinstore]: '15min',
  [ECexName.hotcoin]: '15mins',
  [ECexName.kraken]: 15,
  [ECexName.indodax]: '',
  [ECexName.weex]: '15min',
  [ECexName.biconomy]: '15min',
  [ECexName.coinw]: '900',
  [ECexName.dextrade]: 15,
  [ECexName.digifinex]: '15',
  [ECexName.btse]: '15',
};

const map1h: Record<ECexName, any> = {
  [ECexName.gate]: '1h',
  [ECexName.mexc]: '60m',
  [ECexName.bitget]: '1h',
  [ECexName.kucoin]: '1hour',
  [ECexName.okx]: '1H',
  [ECexName.bybit]: 60,
  [ECexName.bitmart]: 60,
  [ECexName.bithumb]: '60',
  [ECexName.binance]: '1h',
  [ECexName.bingx]: '1h',
  [ECexName.xt]: '1h',
  [ECexName.lbank]: 'hour1',
  [ECexName.htx]: '60min',
  [ECexName.whitebit]: '1h',
  [ECexName.coinbase]: 3600,
  [ECexName.cryptocom]: '1h',
  [ECexName.upbit]: '60',
  [ECexName.bitstamp]: 3600,
  [ECexName.bitvavo]: '1h',
  [ECexName.toobit]: '1h',
  [ECexName.hashkey]: '1h',
  [ECexName.bullish]: '1h',
  [ECexName.gemini]: '1h',
  [ECexName.bitfinex]: '1h',
  [ECexName.coinstore]: '60min',
  [ECexName.hotcoin]: '1h',
  [ECexName.kraken]: 60,
  [ECexName.indodax]: '',
  [ECexName.weex]: '1h',
  [ECexName.biconomy]: 'hour',
  [ECexName.coinw]: '3600',
  [ECexName.dextrade]: 60,
  [ECexName.digifinex]: '60',
  [ECexName.btse]: '60',
};

const map4h: Record<ECexName, any> = {
  [ECexName.gate]: '4h',
  [ECexName.mexc]: '4h',
  [ECexName.bitget]: '4h',
  [ECexName.kucoin]: '4hour',
  [ECexName.okx]: '4H',
  [ECexName.bybit]: 240,
  [ECexName.bitmart]: 240,
  [ECexName.bithumb]: '240',
  [ECexName.binance]: '4h',
  [ECexName.bingx]: '4h',
  [ECexName.xt]: '4h',
  [ECexName.lbank]: 'hour4',
  [ECexName.htx]: '4hour',
  [ECexName.whitebit]: '4h',
  [ECexName.coinbase]: undefined,
  [ECexName.cryptocom]: '4h',
  [ECexName.upbit]: '240',
  [ECexName.bitstamp]: 14400,
  [ECexName.bitvavo]: '4h',
  [ECexName.toobit]: '4h',
  [ECexName.hashkey]: '4h',
  [ECexName.bullish]: undefined,
  [ECexName.gemini]: undefined,
  [ECexName.bitfinex]: undefined,
  [ECexName.coinstore]: '4hour',
  [ECexName.hotcoin]: '4h',
  [ECexName.kraken]: 240,
  [ECexName.indodax]: '',
  [ECexName.weex]: '4h',
  [ECexName.biconomy]: '4hour',
  [ECexName.coinw]: '14400',
  [ECexName.dextrade]: 240,
  [ECexName.digifinex]: '240',
  [ECexName.btse]: '240',
};

const map1d: Record<ECexName, any> = {
  [ECexName.gate]: '1d',
  [ECexName.mexc]: '1d',
  [ECexName.bitget]: '1day',
  [ECexName.kucoin]: '1day',
  [ECexName.okx]: '1D',
  [ECexName.bybit]: 'D',
  [ECexName.bitmart]: '1D',
  [ECexName.bithumb]: 'days',
  [ECexName.binance]: '1d',
  [ECexName.bingx]: '1d',
  [ECexName.xt]: '1d',
  [ECexName.lbank]: 'day1',
  [ECexName.htx]: '1day',
  [ECexName.whitebit]: '1d',
  [ECexName.coinbase]: 86400,
  [ECexName.cryptocom]: '1D',
  [ECexName.upbit]: 'days',
  [ECexName.bitstamp]: 86400,
  [ECexName.bitvavo]: '1d',
  [ECexName.toobit]: '1d',
  [ECexName.hashkey]: '1d',
  [ECexName.bullish]: '1d',
  [ECexName.gemini]: '1d',
  [ECexName.bitfinex]: '1D',
  [ECexName.coinstore]: '1day',
  [ECexName.hotcoin]: '1d',
  [ECexName.kraken]: 1440,
  [ECexName.indodax]: '',
  [ECexName.weex]: '1day',
  [ECexName.biconomy]: 'day',
  [ECexName.coinw]: '86400',
  [ECexName.dextrade]: 1440,
  [ECexName.digifinex]: '1D',
  [ECexName.btse]: '1440',
};

const map1w: Record<ECexName, any> = {
  [ECexName.gate]: '7d',
  [ECexName.mexc]: '1W',
  [ECexName.bitget]: '1week',
  [ECexName.kucoin]: '1week',
  [ECexName.okx]: '1W',
  [ECexName.bybit]: 'W',
  [ECexName.bitmart]: '1D',
  [ECexName.bithumb]: 'weeks',
  [ECexName.binance]: '1w',
  [ECexName.bingx]: '1w',
  [ECexName.xt]: '1w',
  [ECexName.lbank]: 'week1',
  [ECexName.htx]: '1week',
  [ECexName.whitebit]: '1w',
  [ECexName.coinbase]: undefined,
  [ECexName.cryptocom]: '7D',
  [ECexName.upbit]: 'weeks',
  [ECexName.bitstamp]: undefined,
  [ECexName.bitvavo]: '1W',
  [ECexName.toobit]: '1w',
  [ECexName.hashkey]: '1w',
  [ECexName.bullish]: undefined,
  [ECexName.gemini]: undefined,
  [ECexName.bitfinex]: '1W',
  [ECexName.coinstore]: '1week',
  [ECexName.hotcoin]: '1w',
  [ECexName.kraken]: 10080,
  [ECexName.indodax]: '',
  [ECexName.weex]: '1week',
  [ECexName.biconomy]: 'week',
  [ECexName.coinw]: '604800',
  [ECexName.dextrade]: 10080,
  [ECexName.digifinex]: '1W',
  [ECexName.btse]: '10080',
};

export const cexExchangesTimeframes: Record<IChartTimeframe, Record<ECexName, any>> = {
  '1s': mergeIntervals(map1m, {
    [ECexName.gate]: '1s',
    [ECexName.bitstamp]: undefined,
    [ECexName.bitvavo]: undefined,
    [ECexName.bullish]: undefined,
    [ECexName.gemini]: undefined,
  }),
  '10s': mergeIntervals(map1m, {
    [ECexName.gate]: '10s',
    [ECexName.bitstamp]: undefined,
    [ECexName.bitvavo]: undefined,
    [ECexName.bullish]: undefined,
    [ECexName.gemini]: undefined,
  }),
  // Note: Gate.io does NOT support 30s interval - it will use 1m from map1m
  '30s': mergeIntervals(map1m, {
    [ECexName.bitstamp]: undefined,
    [ECexName.bitvavo]: undefined,
    [ECexName.bullish]: undefined,
    [ECexName.gemini]: undefined,
  }),
  '1m': map1m,
  // Note: Gate.io does NOT support 3m interval - it will use 1m from map1m
  '3m': mergeIntervals(map1m, {
    [ECexName.mexc]: '3m',
    [ECexName.bitget]: '3min',
    [ECexName.kucoin]: '3min',
    [ECexName.okx]: '3m',
    [ECexName.bybit]: 3,
    [ECexName.bitmart]: 3,
    [ECexName.binance]: '3m',
    [ECexName.bingx]: '3m',
    [ECexName.xt]: '3m',
    [ECexName.lbank]: 'minute3',
    [ECexName.htx]: '3min',
    [ECexName.whitebit]: '3m',
    [ECexName.coinstore]: '3min',
    [ECexName.hotcoin]: '3mins',
    [ECexName.kraken]: 3,
    [ECexName.upbit]: '3',
    [ECexName.bitstamp]: 180,
    [ECexName.bithumb]: '3',
    [ECexName.bitvavo]: undefined,
    [ECexName.bullish]: undefined,
    [ECexName.gemini]: undefined,
  }),
  '5m': map5m,
  '15m': map15m,
  '30m': mergeIntervals(map5m, {
    [ECexName.gate]: '30m',
    [ECexName.mexc]: '30m',
    [ECexName.bitget]: '30min',
    [ECexName.kucoin]: '30min',
    [ECexName.okx]: '30m',
    [ECexName.bybit]: 30,
    [ECexName.bitmart]: 30,
    [ECexName.binance]: '30m',
    [ECexName.bingx]: '30m',
    [ECexName.xt]: '30m',
    [ECexName.lbank]: 'minute30',
    [ECexName.htx]: '30min',
    [ECexName.whitebit]: '30m',
    [ECexName.coinstore]: '30min',
    [ECexName.hotcoin]: '30mins',
    [ECexName.kraken]: 30,
    [ECexName.upbit]: '30',
    [ECexName.bitstamp]: 1800,
    [ECexName.bithumb]: '30',
    [ECexName.bitvavo]: '30m',
    [ECexName.toobit]: '30m',
    [ECexName.hashkey]: '30m',
    [ECexName.bullish]: '30m',
    [ECexName.gemini]: '30m',
  }),
  '1h': map1h,
  // Note: Gate.io does NOT support 2h interval - it will use 1h from map1h
  '2h': mergeIntervals(map1h, {
    [ECexName.mexc]: '2h',
    [ECexName.bitget]: '2h',
    [ECexName.kucoin]: '2hour',
    [ECexName.okx]: '2H',
    [ECexName.bybit]: 120,
    [ECexName.bitmart]: 120,
    [ECexName.binance]: '2h',
    [ECexName.bingx]: '2h',
    [ECexName.xt]: '2h',
    [ECexName.lbank]: 'hour2',
    [ECexName.htx]: '2hour',
    [ECexName.whitebit]: '2h',
    [ECexName.coinstore]: '2hour',
    [ECexName.hotcoin]: '2h',
    [ECexName.kraken]: 120,
    [ECexName.bitstamp]: 7200,
    [ECexName.bitvavo]: '2h',
    [ECexName.bullish]: undefined,
    [ECexName.gemini]: undefined,
  }),
  '4h': map4h,
  // Note: Gate.io does NOT support 6h interval - it will use 4h from map4h
  '6h': mergeIntervals(map4h, {
    [ECexName.mexc]: '6h',
    [ECexName.bitget]: '6h',
    [ECexName.kucoin]: '6hour',
    [ECexName.okx]: '6H',
    [ECexName.bybit]: 360,
    [ECexName.bitmart]: 360,
    [ECexName.binance]: '6h',
    [ECexName.bingx]: '6h',
    [ECexName.xt]: '6h',
    [ECexName.lbank]: 'hour6',
    [ECexName.htx]: '6hour',
    [ECexName.whitebit]: '6h',
    [ECexName.coinstore]: '6hour',
    [ECexName.hotcoin]: '6h',
    [ECexName.kraken]: 360,
    [ECexName.bitstamp]: 21600,
    [ECexName.bitvavo]: '6h',
    [ECexName.bullish]: '6h',
    [ECexName.gemini]: '6h',
  }),
  '8h': mergeIntervals(map4h, {
    [ECexName.gate]: '8h',
    [ECexName.mexc]: '8h',
    [ECexName.bitget]: '8h',
    [ECexName.kucoin]: '8hour',
    [ECexName.okx]: '8H',
    [ECexName.bybit]: 480,
    [ECexName.bitmart]: 480,
    [ECexName.binance]: '8h',
    [ECexName.bingx]: '8h',
    [ECexName.xt]: '8h',
    [ECexName.lbank]: 'hour8',
    [ECexName.htx]: '8hour',
    [ECexName.whitebit]: '8h',
    [ECexName.coinstore]: '8hour',
    [ECexName.hotcoin]: '8h',
    [ECexName.kraken]: 480,
    [ECexName.bitstamp]: undefined,
    [ECexName.bitvavo]: '8h',
    [ECexName.bullish]: undefined,
    [ECexName.gemini]: undefined,
  }),
  // Note: Gate.io does NOT support 12h interval - it will use 4h from map4h
  '12h': mergeIntervals(map4h, {
    [ECexName.mexc]: '12h',
    [ECexName.bitget]: '12h',
    [ECexName.kucoin]: '12hour',
    [ECexName.okx]: '12H',
    [ECexName.bybit]: 720,
    [ECexName.bitmart]: 720,
    [ECexName.binance]: '12h',
    [ECexName.bingx]: '12h',
    [ECexName.xt]: '12h',
    [ECexName.lbank]: 'hour12',
    [ECexName.htx]: '12hour',
    [ECexName.whitebit]: '12h',
    [ECexName.coinstore]: '12hour',
    [ECexName.hotcoin]: '12h',
    [ECexName.kraken]: 720,
    [ECexName.bitstamp]: 43200,
    [ECexName.bitvavo]: '12h',
    [ECexName.bullish]: '12h',
    [ECexName.gemini]: undefined,
  }),
  '24h': map1d,
  '1d': map1d,
  // Note: Gate.io does NOT support 2d interval - it will use 1d from map1d
  '2d': mergeIntervals(map1d, {
    [ECexName.mexc]: '2d',
    [ECexName.bitget]: '2day',
    [ECexName.kucoin]: '2day',
    [ECexName.okx]: '2D',
    [ECexName.bybit]: '2D',
    [ECexName.bitmart]: '2D',
    [ECexName.binance]: '2d',
    [ECexName.bingx]: '2d',
    [ECexName.xt]: '2d',
    [ECexName.lbank]: 'day2',
    [ECexName.htx]: '2day',
    [ECexName.whitebit]: '2d',
    [ECexName.coinstore]: '2day',
    [ECexName.hotcoin]: '2d',
    [ECexName.kraken]: 2880,
    [ECexName.bitstamp]: undefined,
    [ECexName.bitvavo]: undefined,
    [ECexName.bullish]: undefined,
    [ECexName.gemini]: undefined,
  }),
  // Note: Gate.io does NOT support 3d interval - it will use 1d from map1d
  '3d': mergeIntervals(map1d, {
    [ECexName.mexc]: '3d',
    [ECexName.bitget]: '3day',
    [ECexName.kucoin]: '3day',
    [ECexName.okx]: '3D',
    [ECexName.bybit]: '3D',
    [ECexName.bitmart]: '3D',
    [ECexName.binance]: '3d',
    [ECexName.bingx]: '3d',
    [ECexName.xt]: '3d',
    [ECexName.lbank]: 'day3',
    [ECexName.htx]: '3day',
    [ECexName.whitebit]: '3d',
    [ECexName.coinstore]: '3day',
    [ECexName.hotcoin]: '3d',
    [ECexName.kraken]: 4320,
    [ECexName.bitstamp]: 259200,
    [ECexName.bitvavo]: undefined,
    [ECexName.bullish]: undefined,
    [ECexName.gemini]: undefined,
  }),
  // Note: Gate.io does NOT support 5d interval - it will use 1d from map1d
  '5d': mergeIntervals(map1d, {
    [ECexName.mexc]: '5d',
    [ECexName.bitget]: '5day',
    [ECexName.kucoin]: '5day',
    [ECexName.okx]: '5D',
    [ECexName.bybit]: '5D',
    [ECexName.bitmart]: '5D',
    [ECexName.binance]: '5d',
    [ECexName.bingx]: '5d',
    [ECexName.xt]: '5d',
    [ECexName.lbank]: 'day5',
    [ECexName.htx]: '5day',
    [ECexName.whitebit]: '5d',
    [ECexName.coinstore]: '5day',
    [ECexName.hotcoin]: '5d',
    [ECexName.kraken]: 7200,
    [ECexName.bitstamp]: undefined,
    [ECexName.bitvavo]: undefined,
    [ECexName.bullish]: undefined,
    [ECexName.gemini]: undefined,
  }),
  '168h': map1w,
  '1w': map1w,
  // Note: Gate.io uses '30d' for monthly interval (represents a calendar month)
  '1M': mergeIntervals(map1d, {
    [ECexName.gate]: '30d',
    [ECexName.mexc]: '1M',
    [ECexName.bitget]: '1month',
    [ECexName.kucoin]: '1month',
    [ECexName.okx]: '1M',
    [ECexName.bybit]: 'M',
    [ECexName.bitmart]: '1M',
    [ECexName.bithumb]: 'months',
    [ECexName.binance]: '1M',
    [ECexName.bingx]: '1M',
    [ECexName.xt]: '1M',
    [ECexName.lbank]: 'month1',
    [ECexName.htx]: '1mon',
    [ECexName.whitebit]: '1M',
    [ECexName.coinstore]: '1month',
    [ECexName.hotcoin]: '1M',
    [ECexName.kraken]: 43200,
    [ECexName.upbit]: 'months',
    [ECexName.bitstamp]: undefined,
    [ECexName.bitvavo]: '1M',
    [ECexName.toobit]: '1M',
    [ECexName.hashkey]: '1M',
    [ECexName.bullish]: undefined,
    [ECexName.gemini]: undefined,
  }),
};
